import { ArrowRight, BriefcaseBusiness, Store, CheckCircle2 } from 'lucide-react';
import { Button } from '@zaogig/ui/button';
import { useState } from 'react';

type EntryType = 'worker' | 'enterprise';

interface EntryCardProps {
  type: EntryType;
  title: string;
  description: string;
  buttonText: string;
  onClick: () => void;
}

export function EntryCard({ type, title, description, buttonText, onClick }: EntryCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  const isWorker = type === 'worker';
  const accent = isWorker ? '#10B981' : '#2563EB';
  const softBg = isWorker 
    ? 'from-emerald-50 via-white to-teal-50/50' 
    : 'from-blue-50 via-white to-sky-50/50';
  const buttonClass = isWorker 
    ? 'bg-gradient-to-r from-[#10B981] to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/30' 
    : 'bg-gradient-to-r from-[#2563EB] to-blue-700 hover:from-blue-600 hover:to-blue-800 shadow-blue-500/30';
  const glowClass = isWorker ? 'shadow-emerald-500/20' : 'shadow-blue-500/20';

  return (
    <article
      className={`relative min-h-[280px] sm:min-h-[300px] overflow-hidden rounded-2xl border bg-gradient-to-br ${softBg} p-5 sm:p-6 text-left shadow-lg transition-all duration-300 ${
        isHovered ? `border-white ${glowClass}` : 'border-slate-200/60 shadow-slate-900/8'
      }`}
      style={{ ['--tw-ring-color' as string]: isHovered ? accent : 'transparent' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="absolute right-[-4rem] top-[-4rem] h-[16rem] w-[16rem] rounded-full opacity-15 blur-3xl"
        style={{ backgroundColor: accent }}
      />
      
      <div className="relative flex h-full flex-col">
        <div className="flex items-start justify-between">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-[22px] text-white shadow-xl"
            style={{ 
              backgroundColor: accent,
              boxShadow: `0 10px 40px ${accent}30`
            }}
          >
            {isWorker ? <BriefcaseBusiness className="h-8 w-8" /> : <Store className="h-8 w-8" />}
          </div>
          {isHovered && (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow-sm"
              style={{ color: accent }}
            >
              <CheckCircle2 className="h-4 w-4" />
              已选择
            </div>
          )}
        </div>

        <div className="mt-5">
          <h2 className="text-3xl font-black tracking-tight text-slate-950">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">{description}</p>
        </div>

        <div className="mt-auto pt-4">
          <Button
            onClick={onClick}
            className={`w-full rounded-full px-6 py-4 text-base font-bold text-white shadow-xl transition-all duration-200 hover:-translate-y-1 ${buttonClass}`}
          >
            {buttonText}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </article>
  );
}
